import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Button, MenuItem, Select, FormControl,
  InputLabel, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, TablePagination, Chip, IconButton, Stack, TextField,
  CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  Checkbox, Tooltip,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import PublishIcon from '@mui/icons-material/Publish';
import ArticleIcon from '@mui/icons-material/Article';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  useForm16AdminList,
  useFinancialYears,
  useEmployers,
  useBranches,
  useBulkGenerateForm16,
  useImportSalaryEntries,
  usePublishForm16,
  useEmailForm16,
  previewForm16Emails,
  downloadForm16ExcelSummary,
} from '../../../hooks/useForm16';
import {
  HEAD_CELL_SX, BODY_CELL_SX, TABLE_ROW_SX, TABLE_PAPER_SX,
} from '../../common/tableStyles';
import { useAlert } from '../../../context/AlertContext';
import { SELECTED_MENU_PROPS } from '../../StyledComponents/StyledComponent';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

// ---- FORTHEYE soft-card tokens (shared with the rest of the portal) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';
const OUTLINED_BTN = { textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280', borderColor: '#e5e7eb' };
const GRADIENT_BTN = {
  textTransform: 'none', fontWeight: 700, borderRadius: '12px',
  background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
  '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
};
const INPUT_SX = { '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#f8fafc' } };

// Soft tinted status chip sx (matches the rest of the portal).
const chipSx = (fg, bg) => ({ fontWeight: 700, bgcolor: bg, color: fg, border: 'none' });
const statusChipSx = (status) => {
  const s = String(status).toUpperCase();
  if (s === 'PUBLISHED') return chipSx('#16a34a', '#f0fdf4');
  if (s === 'UNDER_REVIEW') return chipSx('#f59e0b', '#fffbeb');
  if (s === 'REVOKED') return chipSx('#ef4444', '#fef2f2');
  if (s === 'DRAFT') return chipSx('#f59e0b', '#fffbeb');
  return chipSx('#6b7280', '#f3f4f6');
};

export default function Form16ListAdmin() {
  const navigate = useNavigate();
  const { data: fys = [] } = useFinancialYears();
  const { data: employers = [] } = useEmployers();
  const { data: branches = [] } = useBranches();
  const [fyId, setFyId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [branch, setBranch] = useState('');
  const [employeeNo, setEmployeeNo] = useState('');
  const [debouncedEmployeeNo, setDebouncedEmployeeNo] = useState('');
  const [page, setPage] = useState(0);            // 0-indexed for MUI
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedIds, setSelectedIds] = useState(new Set());   // checkbox selection (PUBLISHED rows only)

  // Debounce the free-text Employee No filter so we don't hit the API on every
  // keystroke. Branch is now a select (sourced from the Branch master table),
  // so it applies immediately — no debounce needed.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedEmployeeNo(employeeNo), 400);
    return () => clearTimeout(t);
  }, [employeeNo]);

  // Back to the first page whenever a filter changes. Selection is cleared
  // too — the ids picked under the old filters may no longer be in view.
  useEffect(() => { setPage(0); setSelectedIds(new Set()); }, [fyId, statusFilter, branch, debouncedEmployeeNo]);

  const params = useMemo(() => {
    const p = { page: page + 1, page_size: rowsPerPage };  // API is 1-indexed
    if (fyId) p.fy_id = fyId;
    if (statusFilter) p.status = statusFilter;
    if (branch) p.branch = branch;
    if (debouncedEmployeeNo) p.employee_no = debouncedEmployeeNo;
    return p;
  }, [fyId, statusFilter, branch, debouncedEmployeeNo, page, rowsPerPage]);

  const { data, isLoading, refetch } = useForm16AdminList(params);
  const list = useMemo(() => data?.results || [], [data]);
  const total = data?.count || 0;
  const bulk = useBulkGenerateForm16();

  // Annual summary Excel export — uses the current Financial Year / Branch /
  // Employee No filters (fy_id is required by the endpoint).
  const [excelDownloading, setExcelDownloading] = useState(false);
  const [excelError, setExcelError] = useState(null);
  const fySelected = fys.find((f) => String(f.id) === String(fyId));
  const doDownloadExcel = async () => {
    setExcelError(null);
    setExcelDownloading(true);
    try {
      await downloadForm16ExcelSummary({
        fyId, fyLabel: fySelected?.label, employeeNo: debouncedEmployeeNo, branch,
      });
    } catch (err) {
      setExcelError(err?.response?.data?.error || err.message || 'Could not download the Excel summary.');
    } finally {
      setExcelDownloading(false);
    }
  };

  // Per-row publish (so it's visible on the list, not buried in the cert page).
  const publish = usePublishForm16();
  const [publishingId, setPublishingId] = useState(null);
  const [publishError, setPublishError] = useState(null);
  const doPublish = (id) => {
    setPublishError(null);
    setPublishingId(id);
    publish.mutate(id, {
      onSuccess: () => refetch(),
      onError: (err) => setPublishError(err?.response?.data?.error || err.message),
      onSettled: () => setPublishingId(null),
    });
  };

  // Selection-based email sending (checkbox column + per-row quick action).
  // Only PUBLISHED rows are selectable — the backend rejects anything else.
  const publishedOnPage = useMemo(() => list.filter((r) => r.status === 'PUBLISHED'), [list]);
  const allOnPageSelected = publishedOnPage.length > 0 && publishedOnPage.every((r) => selectedIds.has(r.id));
  const someOnPageSelected = publishedOnPage.some((r) => selectedIds.has(r.id));
  const toggleAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      publishedOnPage.forEach((r) => (allOnPageSelected ? next.delete(r.id) : next.add(r.id)));
      return next;
    });
  };
  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Form 16 email (preview-then-send, mirrors Paylogs.js's payslip email flow).
  const { showWarning, showSuccess, showError } = useAlert();
  const { mutate: emailForm16, isPending: isSendingEmails } = useEmailForm16();
  const [emailDialog, setEmailDialog] = useState({
    open: false, loading: false, payload: null, subtitle: '',
    recipients: [], total: 0, sendable: 0, fromEmail: '',
  });

  const openEmailPreview = async (payload, subtitle) => {
    setEmailDialog({
      open: true, loading: true, payload, subtitle, recipients: [], total: 0, sendable: 0, fromEmail: '',
    });
    try {
      const data = await previewForm16Emails(payload);
      setEmailDialog((d) => ({
        ...d, loading: false,
        recipients: data.recipients || [], total: data.total || 0, sendable: data.sendable || 0,
        fromEmail: data.from_email || '',
      }));
    } catch (e) {
      showError(`Could not load preview: ${e?.response?.data?.error || 'Unknown error'}`);
      setEmailDialog((d) => ({ ...d, open: false, loading: false }));
    }
  };

  const closeEmailDialog = () => setEmailDialog((d) => ({ ...d, open: false }));

  const confirmSendEmails = () => {
    if (!emailDialog.payload) return;
    emailForm16(emailDialog.payload, {
      onSuccess: (data) => {
        const parts = [`Emailed ${data.sent_count} certificate(s)`];
        if (data.skipped_count) parts.push(`${data.skipped_count} skipped`);
        if (data.failed_count) parts.push(`${data.failed_count} failed`);
        if (data.failed_count) showWarning(parts.join(' · '));
        else showSuccess(parts.join(' · '));
        closeEmailDialog();
        setSelectedIds(new Set());
        refetch();
      },
      onError: (e) => showError(`Send failed: ${e?.response?.data?.error || 'Unknown error'}`),
    });
  };

  // Monthly salary Excel import (feeds Form 16, no payslips created).
  const importSalary = useImportSalaryEntries();
  const [importOpen, setImportOpen] = useState(false);
  const [impFile, setImpFile] = useState(null);
  const [impMonth, setImpMonth] = useState('');
  const [impYear, setImpYear] = useState('');
  const [impResult, setImpResult] = useState(null);

  const employer = employers.find((e) => e.is_active) || employers[0];

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* ---- Header card ---- */}
      <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <ArticleIcon />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.7rem' }, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                Form 16 Certificates
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                Generate, review and publish employee Form 16 certificates
              </Typography>
            </Box>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="contained" disableElevation onClick={() => { setImpResult(null); setImportOpen(true); }} sx={GRADIENT_BTN}>
              Import Salary (Excel)
            </Button>
            <Button component={Link} to="/payroll/form16/declarations" variant="outlined" sx={OUTLINED_BTN}>
              Review Declarations
            </Button>
            <Button component={Link} to="/payroll/form16/rectifications" variant="outlined" sx={OUTLINED_BTN}>
              Rectifications
            </Button>
            <Button component={Link} to="/payroll/form16/employer" variant="outlined" sx={OUTLINED_BTN}>
              Employer Setup
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* ---- Filters card ---- */}
      <Paper elevation={0} sx={{ ...CARD_SX, p: { xs: 1.75, sm: 2 }, mb: { xs: 2, md: 3 } }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small" sx={INPUT_SX}>
              <InputLabel>Financial Year</InputLabel>
              <Select label="Financial Year" value={fyId} onChange={(e) => setFyId(e.target.value)} MenuProps={SELECTED_MENU_PROPS}>
                <MenuItem value="">All</MenuItem>
                {fys.map((fy) => (
                  <MenuItem key={fy.id} value={fy.id}>{fy.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small" sx={INPUT_SX}>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} MenuProps={SELECTED_MENU_PROPS}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="DRAFT">Draft</MenuItem>
                <MenuItem value="UNDER_REVIEW">Under Review</MenuItem>
                <MenuItem value="PUBLISHED">Published</MenuItem>
                <MenuItem value="REVOKED">Revoked</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small" sx={INPUT_SX}>
              <InputLabel>Branch</InputLabel>
              <Select label="Branch" value={branch} onChange={(e) => setBranch(e.target.value)} MenuProps={SELECTED_MENU_PROPS}>
                <MenuItem value="">All</MenuItem>
                {branches.map((b) => (
                  <MenuItem key={b.id} value={b.name}>{b.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" label="Employee No"
              value={employeeNo} onChange={(e) => setEmployeeNo(e.target.value)} sx={INPUT_SX} />
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="contained"
                disableElevation
                disabled={!fyId || !employer || bulk.isPending}
                onClick={() => bulk.mutate({ fy_id: fyId, employer_id: employer?.id, branch })}
                sx={GRADIENT_BTN}
              >
                {bulk.isPending ? 'Generating…' : 'Bulk Generate'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon fontSize="small" />}
                disabled={!fyId || excelDownloading}
                onClick={doDownloadExcel}
                sx={OUTLINED_BTN}
              >
                {excelDownloading ? 'Downloading…' : 'Download Excel'}
              </Button>
              <Tooltip title={selectedIds.size ? `Email ${selectedIds.size} selected certificate(s)` : 'Select one or more Published rows to email'}>
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<EmailIcon fontSize="small" />}
                    disabled={selectedIds.size === 0}
                    onClick={() => openEmailPreview(
                      { form16_ids: Array.from(selectedIds) },
                      `${selectedIds.size} Form 16 certificate(s) selected`,
                    )}
                    sx={OUTLINED_BTN}
                  >
                    {selectedIds.size ? `Send Email (${selectedIds.size})` : 'Send Email'}
                  </Button>
                </span>
              </Tooltip>
              <IconButton onClick={() => refetch()} sx={{ color: ACCENT, bgcolor: `${ACCENT}14`, '&:hover': { bgcolor: `${ACCENT}22` } }}><RefreshIcon /></IconButton>
            </Stack>
            {bulk.data && (
              <Alert severity="info" sx={{ mt: 2, borderRadius: '14px' }}>
                Bulk generated {bulk.data.count} certificate(s).
              </Alert>
            )}
            {excelError && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: '14px' }} onClose={() => setExcelError(null)}>
                {excelError}
              </Alert>
            )}
            {!employer && (
              <Alert severity="warning" sx={{ mt: 2, borderRadius: '14px' }}>
                No active Employer master configured. Open <b>Employer Setup</b> to add one.
              </Alert>
            )}
          </Grid>
        </Grid>
      </Paper>

      {publishError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '14px' }} onClose={() => setPublishError(null)}>
          Publish failed: {publishError}
        </Alert>
      )}

      {/* ---- Certificates table ---- */}
      <TableContainer component={Paper} sx={{ ...TABLE_PAPER_SX, borderRadius: '22px', border: '1px solid #eef0f4', boxShadow: '0 6px 24px rgba(17,24,39,0.05)' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={HEAD_CELL_SX}>
                <Checkbox
                  indeterminate={someOnPageSelected && !allOnPageSelected}
                  checked={allOnPageSelected}
                  onChange={toggleAllOnPage}
                  disabled={publishedOnPage.length === 0}
                  sx={{ color: ACCENT, '&.Mui-checked': { color: ACCENT }, '&.MuiCheckbox-indeterminate': { color: ACCENT } }}
                />
              </TableCell>
              <TableCell sx={HEAD_CELL_SX}>Employee</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Branch</TableCell>
              <TableCell sx={HEAD_CELL_SX}>FY</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Period</TableCell>
              <TableCell sx={HEAD_CELL_SX} align="right">Salary</TableCell>
              <TableCell sx={HEAD_CELL_SX} align="right">TDS</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Status</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Rev</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Cert No</TableCell>
              <TableCell sx={HEAD_CELL_SX} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={11} align="center" sx={{ py: 6, borderBottom: 'none' }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : list.length === 0 ? (
              <TableRow><TableCell colSpan={11} align="center" sx={{ borderBottom: 'none' }}>
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Box sx={{ width: 64, height: 64, borderRadius: '18px', bgcolor: '#f3f4f6', color: '#9ca3af', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5 }}>
                    <DescriptionIcon sx={{ fontSize: 30 }} />
                  </Box>
                  <Typography sx={{ color: '#6b7280', fontWeight: 600 }}>No Form 16 records yet.</Typography>
                </Box>
              </TableCell></TableRow>
            ) : list.map((row) => (
              <TableRow key={row.id} sx={TABLE_ROW_SX}>
                <TableCell padding="checkbox" sx={BODY_CELL_SX}>
                  <Checkbox
                    checked={selectedIds.has(row.id)}
                    onChange={() => toggleOne(row.id)}
                    disabled={row.status !== 'PUBLISHED'}
                    sx={{ color: ACCENT, '&.Mui-checked': { color: ACCENT } }}
                  />
                </TableCell>
                <TableCell sx={BODY_CELL_SX}>
                  <Typography variant="body2" fontWeight={600}>{row.employee_name}</Typography>
                  <Typography variant="caption" color="text.secondary">{row.employee_no}</Typography>
                </TableCell>
                <TableCell sx={BODY_CELL_SX}>{row.branch_name || '—'}</TableCell>
                <TableCell sx={BODY_CELL_SX}>{row.fy_label}</TableCell>
                <TableCell sx={BODY_CELL_SX}>{row.period_from} → {row.period_to}</TableCell>
                <TableCell sx={BODY_CELL_SX} align="right">{Number(row.total_salary).toLocaleString('en-IN')}</TableCell>
                <TableCell sx={BODY_CELL_SX} align="right">{Number(row.total_tds).toLocaleString('en-IN')}</TableCell>
                <TableCell sx={BODY_CELL_SX}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Chip size="small" label={row.status} sx={statusChipSx(row.status)} />
                    {row.email_sent_at && (
                      <Tooltip title={`Emailed on ${row.email_sent_at}`}>
                        <Chip
                          icon={<EmailIcon sx={{ fontSize: 13 }} />}
                          label="Emailed"
                          size="small"
                          sx={{
                            fontWeight: 700, fontSize: '10px', height: 20, border: 'none',
                            bgcolor: '#f0fdf4', color: '#16a34a',
                            '& .MuiChip-icon': { color: '#16a34a', ml: '4px' },
                          }}
                        />
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
                <TableCell sx={BODY_CELL_SX}>R{row.revision_no}</TableCell>
                <TableCell sx={BODY_CELL_SX}>{row.certificate_no || '—'}</TableCell>
                <TableCell sx={BODY_CELL_SX} align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                    {row.status === 'PUBLISHED' && (
                      <Tooltip title="Send Form 16 to email">
                        <IconButton
                          size="small"
                          onClick={() => openEmailPreview(
                            { form16_id: row.id },
                            `${row.employee_name} — FY ${row.fy_label}`,
                          )}
                          sx={{ color: ACCENT, bgcolor: `${ACCENT}14`, '&:hover': { bgcolor: `${ACCENT}22` } }}
                        >
                          <EmailIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {row.status !== 'PUBLISHED' && (
                      <Button
                        size="small" variant="outlined" startIcon={<PublishIcon fontSize="small" />}
                        disabled={publishingId === row.id}
                        onClick={() => doPublish(row.id)}
                        sx={{ ...OUTLINED_BTN, py: 0.2 }}
                      >
                        {publishingId === row.id ? 'Publishing…' : 'Publish'}
                      </Button>
                    )}
                    <IconButton size="small" title="Open certificate" onClick={() => navigate(`/payroll/form16/${row.id}`)} sx={{ color: ACCENT }}>
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {total > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        )}
      </TableContainer>

      <Dialog open={importOpen} onClose={() => setImportOpen(false)} fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: '22px', p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Import Monthly Salary (Excel)</DialogTitle>
        <DialogContent dividers>
          <Typography variant="caption" color="text.secondary">
            Upload one month's salary sheet. This feeds Form 16 directly — no payslips are created.
            Re-uploading the same month overwrites it.
          </Typography>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" sx={INPUT_SX}>
                <InputLabel>Month</InputLabel>
                <Select label="Month" value={impMonth} onChange={(e) => setImpMonth(e.target.value)} MenuProps={SELECTED_MENU_PROPS}>
                  {MONTHS.map((m) => (<MenuItem key={m} value={m}>{m}</MenuItem>))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="number" label="Year"
                value={impYear} onChange={(e) => setImpYear(e.target.value)} sx={INPUT_SX} />
            </Grid>
            <Grid item xs={12}>
              <input type="file" accept=".xlsx"
                onChange={(e) => setImpFile(e.target.files?.[0] || null)} />
            </Grid>
          </Grid>
          {importSalary.isError && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: '14px' }}>
              {importSalary.error?.response?.data?.error || importSalary.error?.message || 'Import failed.'}
            </Alert>
          )}
          {impResult && (
            <Alert severity="success" sx={{ mt: 2, borderRadius: '14px' }}>
              {impResult.month} {impResult.year}: created {impResult.created}, updated {impResult.updated},
              skipped {impResult.skipped}.
              {impResult.unmatched_employee_nos?.length > 0 && (
                <> Unmatched employee no(s): {impResult.unmatched_employee_nos.join(', ')}.</>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setImportOpen(false)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280' }}>Close</Button>
          <Button variant="contained"
            disableElevation
            disabled={!impFile || !impMonth || !impYear || importSalary.isPending}
            onClick={() => {
              setImpResult(null);
              importSalary.mutate(
                { file: impFile, month: impMonth, year: impYear },
                { onSuccess: (res) => { setImpResult(res); refetch(); } },
              );
            }}
            sx={GRADIENT_BTN}>
            {importSalary.isPending ? 'Importing…' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Form 16 email preview / confirm dialog ---- */}
      <Dialog
        open={emailDialog.open}
        onClose={closeEmailDialog}
        maxWidth="md"
        fullWidth
        scroll="paper"
        // The global admin header is position:fixed above MUI's default dialog
        // z-index, so lift the whole modal above it. See HRP-FE-19.
        sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
        PaperProps={{
          sx: {
            maxHeight: { xs: '100dvh', sm: 'calc(100vh - 32px)' },
            height: { xs: '100dvh', sm: 'auto' },
            width: { xs: '100%', sm: 'calc(100% - 32px)' },
            m: { xs: 0, sm: 2 },
            borderRadius: { xs: 0, sm: '22px' },
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, flexShrink: 0 }}>
          <EmailIcon sx={{ color: ACCENT }} /> Send Form 16 Email
        </DialogTitle>
        <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 } }}>
          {emailDialog.subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {emailDialog.subtitle}
            </Typography>
          )}
          {emailDialog.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (() => {
            const recips = emailDialog.recipients;
            const missing = recips.filter((r) => r.status === 'missing').length;
            const dup = recips.filter((r) => r.status === 'duplicate').length;
            const noPdf = recips.filter((r) => r.status === 'ok' && !r.has_pdf).length;
            const severity = emailDialog.sendable === 0 ? 'error' : (missing || dup || noPdf) ? 'warning' : 'success';
            const isSingle = recips.length === 1;
            const sample = recips.find((r) => r.sendable) || recips[0];
            return (
              <>
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>From:</strong> {emailDialog.fromEmail || '—'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>To:</strong>{' '}
                    {isSingle
                      ? (sample?.email || '— none —')
                      : `${emailDialog.sendable} employee mailbox(es) — each receives their own Form 16`}
                  </Typography>
                  {sample?.subject && (
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Subject:</strong> {sample.subject}
                    </Typography>
                  )}
                  {isSingle && sample?.attachment_name && (
                    <Typography variant="body2">
                      <strong>Attachment:</strong> 📎 {sample.attachment_name}
                    </Typography>
                  )}
                </Box>

                <Alert severity={severity} icon={severity === 'warning' ? <WarningAmberIcon /> : undefined} sx={{ mb: 2, borderRadius: '14px' }}>
                  <strong>{emailDialog.sendable}</strong> of {emailDialog.total} Form 16 certificate(s) will be emailed.
                  {missing ? ` ${missing} have no valid email and will be skipped.` : ''}
                  {noPdf ? ` ${noPdf} have no PDF generated yet and will be skipped.` : ''}
                  {dup ? ` ${dup} share a duplicate address — review carefully, the wrong person may receive a certificate.` : ''}
                </Alert>

                {/* The actual email body that will be delivered to the employee */}
                {sample?.html && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: '#374151' }}>
                      Email {isSingle ? '' : '(sample — personalised per employee)'}
                    </Typography>
                    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
                      <iframe
                        title="form16-email-preview"
                        srcDoc={sample.html}
                        style={{ width: '100%', height: 'min(360px, 45dvh)', border: 'none' }}
                      />
                    </Box>
                  </Box>
                )}

                {/* Recipient safety table (bulk only — single recipient already shown above) */}
                {!isSingle && (
                  <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #eee', borderRadius: 2, maxHeight: 320 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Emp No</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>FY</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {recips.map((r) => {
                          const chip = r.sendable
                            ? { label: 'Will send', color: '#2e7d32', bg: '#e8f5e9' }
                            : r.status === 'duplicate'
                              ? { label: 'Duplicate', color: '#ed6c02', bg: '#fff4e5' }
                              : !r.has_pdf
                                ? { label: 'No PDF', color: '#d32f2f', bg: '#fdecea' }
                                : { label: 'No email', color: '#d32f2f', bg: '#fdecea' };
                          return (
                            <TableRow key={r.form16_id}>
                              <TableCell>{r.name}</TableCell>
                              <TableCell>{r.employee_no}</TableCell>
                              <TableCell sx={{ color: r.email ? 'inherit' : '#d32f2f' }}>{r.email || '— none —'}</TableCell>
                              <TableCell>{r.financial_year}</TableCell>
                              <TableCell align="center">
                                <Chip size="small" label={chip.label} sx={{ bgcolor: chip.bg, color: chip.color, fontWeight: 700 }} />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            gap: { xs: 1, sm: 0 },
            '& > :not(style) ~ :not(style)': { ml: { xs: 0, sm: 1 } },
          }}
        >
          <Button onClick={closeEmailDialog} disabled={isSendingEmails} sx={{ textTransform: 'none', width: { xs: '100%', sm: 'auto' }, minHeight: 44 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={isSendingEmails ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SendIcon />}
            disabled={emailDialog.loading || isSendingEmails || emailDialog.sendable === 0}
            onClick={confirmSendEmails}
            sx={{ ...GRADIENT_BTN, px: 2, width: { xs: '100%', sm: 'auto' }, minHeight: 44 }}
          >
            {isSendingEmails ? 'Sending…' : `Send to ${emailDialog.sendable} mailbox(es)`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
